import { FC, Fragment, useMemo, useState } from 'react';
import { Divider, Table, Tabs, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { getCustomerDetail } from '@/libs/fleet-api/services/customer';
import { formatCurrency, formatNPWPValue } from '@/libs/antd/formater';
import dayjs from 'dayjs';

export type CustomerDetailModalState = null | {
	customerId: string;
};

export type CustomerDetailModalProps = {
	modal: CustomerDetailModalState;
	onClose: () => void;
};

const CustomerDetailModal: FC<CustomerDetailModalProps> = ({
	modal,
	onClose,
}) => {
	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['getCustomerDetail', modal?.customerId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getCustomerDetail(id);
		},
	});

	const {
		customerInformation,
		customerCompanyManagementInformation,
		customerCompanyShareholderInformation,
		externalCustomerData,
		customerHistory,
		prospects,
	} = data?.data ?? {};

	const customerExposure = useMemo(() => {
		if (!externalCustomerData || typeof externalCustomerData !== 'string') {
			return null;
		}

		try {
			return JSON.parse(externalCustomerData)?.CustomerExposure as {
				ActiveAgreement: number;
				AssetInventoried: number;
				AssetRepossesed: number;
				InProcessAgreement: number;
				MaxInstallmentAmt: number;
				MaxOSBalance: number;
				MaxOverdueAmt: number;
				MaxOverdueDays: number;
				NamaCustomer: string;
				OSBalanceAR: number;
				OSInterest: number;
				OSPrincipal: number;
			};
		} catch {
			return null;
		}
	}, [externalCustomerData]);

	return (
		<FleetModal
			open={Boolean(modal)}
			width={940}
			cancelText="BACK"
			buttonPosition="end"
			closable={false}
			okButtonProps={{
				hidden: true,
			}}
			onCancel={() => onClose?.()}
		>
			<FleetTitle>Detail Customer</FleetTitle>
			<Tabs
				items={[
					{
						key: 'Data Perusahaan',
						label: 'Data Perusahaan',
						children: (
							<Fragment>
								<DataRow
									loading={isLoading}
									items={[
										[
											[
												'Customer Type',
												customerInformation?.customerType?.value ?? '-',
											],
										],
										[
											['Customer ID', modal?.customerId],
											['Nama Customer', customerInformation?.customerName],
										],
										[
											[
												'Jenis Badan Usaha',
												customerInformation?.businessEntity?.value ?? '-',
											],
											[
												'Jenis Perusahaan',
												customerInformation?.companyType?.value ?? '-',
											],
										],
										[
											[
												'NPWP',
												customerInformation?.npwpNo &&
													formatNPWPValue(customerInformation.npwpNo),
											],
											['Email Perusahaan', customerInformation?.companyEmail],
										],
										[
											[
												'Alamat Perusahaan',
												customerInformation?.companyAddress,
											],
											[
												'RT/RW',
												`${customerInformation?.rt} / ${customerInformation?.rw}`,
											],
										],
										[
											[
												'Kode Pos',
												customerInformation?.address?.postalCode ?? '-',
											],
											[
												'Kelurahan',
												customerInformation?.address?.locationName ?? '-',
											],
										],
										[
											[
												'Kecamatan',
												customerInformation?.address?.districtName ?? '-',
											],
											['Kota', customerInformation?.address?.cityName ?? '-'],
										],
										[
											[
												'Sektor Industri Level 5',
												customerInformation?.industrySector?.sectorLevel5 ??
													'-',
											],
											[
												'Sektor Industri Level 4',
												customerInformation?.industrySector?.sectorLevel4 ??
													'-',
											],
										],
										[
											[
												'Sektor Industri Level 3',
												customerInformation?.industrySector?.sectorLevel3 ??
													'-',
											],
											[
												'Sektor Industri Level 2',
												customerInformation?.industrySector?.sectorLevel2 ??
													'-',
											],
										],
										[
											[
												'Sektor Industri Level 1',
												customerInformation?.industrySector?.sectorLevel1 ??
													'-',
											],
											[
												'Kelas Industri',
												customerInformation?.industrySector?.industryClass ??
													'-',
											],
										],
										[
											[
												'Ekosistem',
												customerInformation?.industrySector?.ecosystem ?? '-',
											],
											[
												'Portofolio Guideline',
												customerInformation?.industrySector
													?.portofolioGuideline ?? '-',
											],
										],
										[
											['Nama PIC Customer', customerInformation?.picName],
											[
												'Jabatan',
												customerInformation?.picPosition?.value ?? '-',
											],
										],
										[
											[
												'Group Customer BMPP',
												customerInformation?.customerGroup?.value,
											],
										],
									]}
									style={{
										marginBottom: 20,
									}}
								/>

								<FleetTitle>Management/Shareholder Listing</FleetTitle>
								<Typography.Title
									level={3}
									style={{ padding: '10px 20px' }}
								>
									Management Listing
								</Typography.Title>
								<Table
									bordered
									loading={isLoading || isFetching}
									columns={[
										{
											title: 'Nama',
											dataIndex: ['name'],
										},
										{
											title: 'Tipe Identitas',
											dataIndex: ['identityCardType', 'value'],
										},
										{
											title: 'Nomor Identitas',
											dataIndex: ['identityNumber'],
										},
										{
											title: 'Jabatan',
											dataIndex: ['position', 'value'],
										},
										{
											title: 'Alamat',
											dataIndex: 'address',
										},
									]}
									dataSource={
										customerCompanyManagementInformation?.map((data, key) => {
											return { ...data, key };
										}) ?? []
									}
									pagination={false}
									style={{ marginBottom: 20 }}
								/>
								<Typography.Title
									level={3}
									style={{ padding: '10px 20px' }}
								>
									Shareholder Listing
								</Typography.Title>
								<Table
									bordered
									loading={isLoading || isFetching}
									columns={[
										{
											title: 'Nama',
											dataIndex: ['name'],
										},
										{
											title: 'Tipe Identitas',
											dataIndex: ['identityCardType', 'value'],
										},
										{
											title: 'Nomor Identitas',
											dataIndex: ['identityNumber'],
										},
										{
											title: 'Kepemilikan Saham (%)',
											render: (_, record) => `${record.shareholding || 0}%`,
										},
										{
											title: 'Alamat',
											dataIndex: 'address',
										},
									]}
									dataSource={
										customerCompanyShareholderInformation?.map((data, key) => {
											return { ...data, key };
										}) ?? []
									}
									pagination={false}
									style={{ marginBottom: 20 }}
								/>
								<FleetTitle>Prospect Listing</FleetTitle>
								<Table
									bordered
									loading={isLoading || isFetching}
									columns={[
										{
											title: 'No',
											align: 'center',
											width: 1,
											dataIndex: ['no'],
										},
										{
											title: 'Prospect ID',
											dataIndex: ['prospectId'],
										},
										{
											title: 'Prospect ID Date',
											render: (_, record) =>
												dayjs(record.prospectDateAt).format('DD/MM/YYYY'),
										},
										{
											title: 'Progress',
											dataIndex: ['progressGroup', 'value'],
										},
										{
											title: 'Status',
											render: (_, record) => {
												if (record.reviewStatus === 'Reject') {
													return <span style={{ color: 'red' }}>Reject</span>;
												}
												return <span>Active</span>;
											},
										},
									]}
									dataSource={
										prospects?.map((data, key) => {
											return { ...data, key };
										}) ?? []
									}
									pagination={false}
									style={{ marginBottom: 20 }}
								/>
								<FleetTitle>Update History</FleetTitle>
								<Table
									bordered
									loading={isLoading || isFetching}
									columns={[
										{
											title: 'No',
											align: 'center',
											width: 1,
											dataIndex: ['no'],
										},
										{
											title: 'Name',
											dataIndex: ['userName'],
										},
										{
											title: 'Date',
											render: (_, record) =>
												dayjs(record.date).format('DD/MM/YYYY'),
										},
										{
											title: 'Process',
											dataIndex: ['event'],
										},
										{
											title: 'Notes',
											render: (_, record) => record.notes ?? '-',
										},
									]}
									dataSource={
										customerHistory?.map((data, key) => {
											return { ...data, key };
										}) ?? []
									}
									pagination={false}
									style={{ marginBottom: 50 }}
								/>
							</Fragment>
						),
					},
					{
						key: 'Customer Exposure',
						label: 'Customer Exposure',
						children: (
							<Fragment>
								<DataRow
									col={24}
									labelCol={{ span: 6 }}
									wrapperCol={{ span: 18 }}
									items={[
										[
											[
												'Nama Customer',
												<div style={{ marginLeft: -10 }}>
													{customerExposure?.NamaCustomer}
												</div>,
											],
										],
									]}
								/>
								<Divider />
								<Typography.Title level={5}>
									Outstanding Amount
								</Typography.Title>

								<DataRow
									wrapperCol={{ span: 12 }}
									labelCol={{ span: 12 }}
									items={[
										[
											[
												'Outstanding Balance A/R',
												customerExposure?.OSBalanceAR
													? formatCurrency(customerExposure?.OSBalanceAR)
													: '-',
											],
											[
												'Outstanding Principal',
												customerExposure?.OSPrincipal
													? formatCurrency(customerExposure?.OSPrincipal)
													: '-',
											],
										],
										[
											[
												'Outstanding Interest',
												customerExposure?.OSInterest
													? formatCurrency(customerExposure?.OSInterest)
													: '-',
											],
										],
									]}
								/>
								<Typography.Title level={5}>Maximum Amount</Typography.Title>
								<DataRow
									wrapperCol={{ span: 12 }}
									labelCol={{ span: 12 }}
									items={[
										[
											[
												'Outstanding Balance',
												formatCurrency(customerExposure?.MaxOSBalance),
											],
											[
												'Installment Amount',
												formatCurrency(customerExposure?.MaxInstallmentAmt),
											],
										],
										[
											['OverDue Days', customerExposure?.MaxOverdueDays ?? '-'],
											[
												'OverDue Amount',
												formatCurrency(customerExposure?.MaxOverdueAmt),
											],
										],
									]}
								/>
								<Typography.Title level={5}>Number Of</Typography.Title>
								<DataRow
									wrapperCol={{ span: 12 }}
									labelCol={{ span: 12 }}
									items={[
										[
											[
												'Active Agreement',
												customerExposure?.ActiveAgreement ?? '-',
											],
											[
												'In Process Agreement',
												customerExposure?.ActiveAgreement,
											],
										],
										[
											[
												'Asset Repossessed',
												customerExposure?.AssetRepossesed ?? '-',
											],
											[
												'Asset Inventoried',
												customerExposure?.AssetInventoried ?? '-',
											],
										],
									]}
								/>
							</Fragment>
						),
					},
					// {
					// 	key: 'Group Exposure',
					// 	label: 'Group Exposure',
					// 	children: 'Group Exposure',
					// },
				]}
			/>
		</FleetModal>
	);
};

export default CustomerDetailModal;

export const useCustomerDetailModal = () => {
	return useState<CustomerDetailModalState>(null);
};
